import { useEffect, useState } from 'react';

import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import type { SxProps, Theme } from '@mui/material/styles';

import Input from 'shared/atoms/Input';
import type { BackgroundThemes } from 'shared/const';
import theme from 'shared/themes/default';

import FormLabel from '../components/FormLabel';

interface FormFieldProperties {
  disabled?: boolean;
  maxLength?: number;
  mode?: string;
  pattern?: string;
  required?: boolean;
  type?: string;
}

interface FormInputProps {
  disabled?: boolean;
  errors?: Record<string, string>;
  handleBlur?: (params: { name: string }) => void;
  handleChange?: (params: { name: string; value: unknown }) => void;
  id: string;
  infoText?: string;
  label: string;
  locale?: string;
  maskValue?: boolean;
  name: string;
  properties?: Record<string, FormFieldProperties>;
  required?: boolean;
  showMaxLength?: boolean;
  sx?: SxProps<Theme>;
  theme?: BackgroundThemes;
  value?: string; // TODO: Only used for forms that cannot use useForm yet - to be removed
  values?: Record<string, unknown>;
}

const FormInput = ({
  disabled: disabledProp,
  errors = {},
  handleBlur,
  handleChange,
  id: idProp,
  infoText,
  label,
  locale,
  maskValue = false,
  name: nameProp,
  properties = {},
  required: requiredProp,
  showMaxLength,
  sx = {},
  theme: themeLayout = 'dark',
  value: valueProp,
  values = {},
  ...props
}: FormInputProps) => {
  const id = locale ? `${idProp}#${locale}` : idProp;
  const name = locale ? `${nameProp}#${locale}` : nameProp;

  const [charCount, setCharCount] = useState(0);

  useEffect(() => {
    const currentValue =
      name in values && typeof values[name] === 'string'
        ? values[name]
        : valueProp;
    setCharCount(currentValue?.length || 0);
  }, [name, values, valueProp]);

  const { disabled, maxLength, mode, pattern, required, type } =
    properties[name] || {};

  const onBlurHandler = ({
    target: { name: targetName },
  }: React.FocusEvent<HTMLInputElement>) => {
    handleBlur?.({ name: targetName });
  };

  const onChangeHandler = ({
    target: { name: targetName, value },
  }: React.ChangeEvent<HTMLInputElement>) => {
    handleChange?.({ name: targetName, value });
    setCharCount(value.length);
  };

  const sxHelperText: SxProps<Theme> = {
    fontSize: '0.875rem',
    lineHeight: '140%',
    fontWeight: 500,
    margin: `${theme.spacing.xs} 0 0 0`,
    '&.Mui-error': {
      color:
        themeLayout === 'light'
          ? theme.palette.errorDark
          : theme.palette.errorLight,
    },
  };

  const currentValue =
    name in values && typeof values[name] === 'string'
      ? values[name]
      : valueProp;

  return (
    <FormControl
      disabled={disabledProp === undefined ? disabled : disabledProp}
      error={!!errors[name]}
      fullWidth
      required={requiredProp === undefined ? required : requiredProp}
      sx={sx}
    >
      <FormLabel
        id={id}
        infoText={infoText}
        label={label}
        length={charCount}
        maxLength={showMaxLength ? maxLength : undefined}
        theme={themeLayout}
      />
      <Input
        id={id}
        inputMode={
          mode as
            | 'text'
            | 'decimal'
            | 'numeric'
            | 'tel'
            | 'search'
            | 'email'
            | 'url'
            | undefined
        }
        inputType={type}
        maxLength={maxLength}
        name={name}
        onBlur={onBlurHandler}
        onChange={onChangeHandler}
        pattern={pattern}
        theme={themeLayout}
        value={currentValue}
        data-matomo-mask={maskValue}
        {...props}
      />
      {errors[name] && (
        <FormHelperText sx={sxHelperText}>{errors[name]}</FormHelperText>
      )}
    </FormControl>
  );
};

export default FormInput;
export type { FormInputProps, FormFieldProperties };
